import { supabase } from '../../lib/supabase'
import { schedulingServiceEnabled } from '../../lib/features'

// muster.notification_event (20260923000000_notifications.sql,
// 20260924000000_schedule_publication_enum.sql,
// 20260927020000_leave_request_cancelled_enum.sql,
// 20261001000000_leave_decision_reversal_enum.sql,
// 20261001020000_schedule_decision_reversal_enum.sql).
export type NotificationEventKind =
  | 'leave_request_submitted'
  | 'leave_request_approved'
  | 'leave_request_returned'
  | 'leave_request_denied'
  | 'leave_request_cancelled'
  | 'leave_request_decision_reversed'
  | 'availability_exception_submitted'
  | 'availability_exception_resubmitted'
  | 'availability_exception_approved'
  | 'availability_exception_returned'
  | 'availability_exception_denied'
  | 'open_shift_posted'
  | 'open_shift_claimed'
  | 'open_shift_claim_approved'
  | 'open_shift_claim_denied'
  | 'giveaway_posted'
  | 'giveaway_filled'
  | 'schedule_reminder'
  | 'shift_swap_proposed'
  | 'shift_swap_accepted'
  | 'shift_swap_declined'
  | 'shift_swap_ready'
  | 'shift_swap_approved'
  | 'shift_swap_denied'
  | 'leave_request_resubmitted'
  | 'schedule_submission_submitted'
  | 'schedule_submission_approved'
  | 'schedule_submission_returned'
  | 'schedule_submission_denied'
  | 'schedule_submission_decision_reversed'
  | 'schedule_submission_resubmitted'
  | 'schedule_published'
  | 'schedule_amended'

// Events that belong to the scheduling service. Their links lead to pages that
// are not routed when the service is off, so the feed leaves them out.
export const SCHEDULING_SERVICE_EVENTS: NotificationEventKind[] = [
  'availability_exception_submitted',
  'availability_exception_resubmitted',
  'availability_exception_approved',
  'availability_exception_returned',
  'availability_exception_denied',
  'open_shift_posted',
  'open_shift_claimed',
  'open_shift_claim_approved',
  'open_shift_claim_denied',
  'giveaway_posted',
  'giveaway_filled',
  'schedule_reminder',
  'shift_swap_proposed',
  'shift_swap_accepted',
  'shift_swap_declined',
  'shift_swap_ready',
  'shift_swap_approved',
  'shift_swap_denied',
]

export type AppNotification = {
  id: string
  eventKind: NotificationEventKind
  summary: string
  link: string
  readAt: string | null
  createdAt: string
}

type NotificationRow = {
  id: string
  event_kind: NotificationEventKind
  summary: string
  link: string
  subject_id: string | null
  read_at: string | null
  created_at: string
}

const NOTIFICATION_COLUMNS = 'id, event_kind, summary, link, subject_id, read_at, created_at'

// The signed-in account's own feed, newest first, per
// docs/product/notifications.md's "notification list shows each event
// with its time, a one-line summary, and a link to the record." Capped at
// 30: AppHeader.tsx's own dropdown is a quick glance, not a full history,
// and nothing in this pilot reads further back than that today.
// A supervisor's notification about a request opens the approval inbox at that
// request; the stored link names the page, and the record it concerns is the
// notification's subject.
const APPROVAL_INBOX = '/supervisor/approval-inbox'
function linkFor(row: NotificationRow): string {
  return row.link === APPROVAL_INBOX && row.subject_id ? `${APPROVAL_INBOX}?request=${row.subject_id}` : row.link
}

export async function getMyNotifications(): Promise<AppNotification[]> {
  // Filtered in the query, so the events left out do not use up the 30.
  let query = supabase.from('notifications').select(NOTIFICATION_COLUMNS)
  if (!schedulingServiceEnabled()) query = query.not('event_kind', 'in', `(${SCHEDULING_SERVICE_EVENTS.join(',')})`)
  const { data, error } = await query.order('created_at', { ascending: false }).limit(30)
  if (error || !data) return []

  return (data as unknown as NotificationRow[]).map((row) => ({
    id: row.id,
    eventKind: row.event_kind,
    summary: row.summary,
    link: linkFor(row),
    readAt: row.read_at,
    createdAt: row.created_at,
  }))
}

// "Reading the record marks the notification read" (notifications.md):
// called the moment a notification is opened, not before, so a badge
// count reflects only what an account has actually seen. Returns whether
// the write landed, the same as every other decide/save function in this
// workspace, though AppHeader.tsx's own caller treats a failure as
// non-blocking: the navigation it accompanies still happens either way.
export async function markNotificationRead(id: string): Promise<boolean> {
  const { error } = await supabase.from('notifications').update({ read_at: new Date().toISOString() }).eq('id', id)
  return !error
}
